import { useState } from 'react';
import { Send, LogIn } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import type { Comment } from '@/hooks/useComments';

interface CommentSectionProps {
  comments: Comment[];
  isLoading: boolean;
  onAdd: (text: string) => void;
  isPending: boolean;
}

export function CommentSection({ comments, isLoading, onAdd, isPending }: CommentSectionProps) {
  const { user } = useAuth();
  const [text, setText] = useState('');

  const handleSubmit = () => {
    if (!text.trim()) return;
    onAdd(text.trim());
    setText('');
  };

  const timeAgo = (date: string) => {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  return (
    <div className="mt-3 border-t border-border pt-3">
      {isLoading ? (
        <p className="text-[10px] text-muted-foreground font-body">Loading...</p>
      ) : comments.length > 0 ? (
        <div className="space-y-2 max-h-40 overflow-y-auto mb-2">
          {comments.map(c => (
            <div key={c.id} className="flex gap-2">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-secondary text-xs">{c.profile_emoji}</span>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-medium text-foreground font-body">{c.profile_username}</span>
                  <span className="text-[9px] text-muted-foreground font-body">{timeAgo(c.created_at)}</span>
                </div>
                <p className="text-xs text-foreground/90 font-body leading-relaxed">{c.text_content}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[10px] text-muted-foreground font-body mb-2">No comments yet</p>
      )}
      {user ? (
        <div className="flex gap-2">
          <input
            type="text"
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            placeholder="Add a comment..."
            className="flex-1 rounded-lg border border-border bg-secondary px-3 py-1.5 text-xs text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <button
            onClick={handleSubmit}
            disabled={!text.trim() || isPending}
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground disabled:opacity-50"
          >
            <Send className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <p className="text-[10px] text-muted-foreground font-body flex items-center gap-1.5">
          <LogIn className="h-3 w-3 text-primary" /> Sign in to comment
        </p>
      )}
    </div>
  );
}
